import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ShoppingCart, Shuffle, ArrowRight } from 'lucide-react';
import { MediaRotator } from './MediaRotator';

const BREAK_TEAM_OWNER_IMAGE = 'https://customer-assets.emergentagent.com/job_widget-studio-19/artifacts/ghqyn0j9_Screenshot%202026-06-01%20164913.png';
const BREAK_TEAM_OWNER_VIDEO = 'https://customer-assets.emergentagent.com/job_widget-studio-19/artifacts/w5kfm2dg_Screen%20Recording%202026-06-01%20203143.mp4';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const WidgetGallery = () => {
  const [widgets, setWidgets] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchWidgets = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/widgets`);
      setWidgets(data);
    } catch (error) {
      console.error('Failed to fetch widgets:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchWidgets();
  }, [fetchWidgets]);

  const scrollToContact = () => {
    const el = document.getElementById('contact');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-20" data-testid="widget-gallery-loading">
        <div className="text-muted">Loading widgets...</div>
      </div>
    );
  }

  return (
    <section id="widgets" className="py-24 px-4 sm:px-6 lg:px-8" data-testid="widget-gallery">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-14">
          <span className="eyebrow" data-testid="widget-gallery-badge">Ready-Made Widgets</span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mt-4 mb-4 text-strong" data-testid="widget-gallery-heading">
            Stream Widgets Shop
          </h2>
          <p className="text-base text-muted max-w-2xl mx-auto" data-testid="widget-gallery-description">
            Pre-built widgets for OBS. Tap any card to request it — we'll take it from there.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {widgets.map((widget) => {
            // Map widgets that have dedicated product pages to those instead of the contact form.
            const productPageByWidget = {
              'widget-chat-overlay': '/widgets/team-randomizer',
              'widget-scoreboard': '/widgets/team-spot-owner',
              'widget-alert-banner': '/widgets/coin-flip-machine',
            };
            const productPage = productPageByWidget[widget.id];
            const isProductPage = Boolean(productPage);
            const cardProps = isProductPage
              ? { as: Link, to: productPage }
              : { as: 'button', type: 'button', onClick: scrollToContact };
            const Wrapper = cardProps.as;
            const wrapperRest = isProductPage
              ? { to: cardProps.to }
              : { type: cardProps.type, onClick: cardProps.onClick };
            // Reuse "isRandomizer" semantics for any widget with a product page —
            // these show "Learn more" instead of "Request".
            const isRandomizer = isProductPage;
            return (
              <Wrapper
                key={widget.id}
                className="text-left widget-card cursor-pointer focus:outline-none block"
                data-testid={`widget-card-${widget.id}`}
                {...wrapperRest}
              >
              <div className="aspect-video bg-[#0F1113] overflow-hidden">
                {widget.id === 'widget-scoreboard' ? (
                  <MediaRotator
                    imageSrc={BREAK_TEAM_OWNER_IMAGE}
                    videoSrc={BREAK_TEAM_OWNER_VIDEO}
                    alt={widget.name}
                    className="w-full h-full"
                    testId={`widget-media-${widget.id}`}
                  />
                ) : (
                  <img
                    src={widget.image_url}
                    alt={widget.name}
                    className="w-full h-full object-cover"
                    data-testid={`widget-image-${widget.id}`}
                  />
                )}
              </div>
              <div className="p-6">
                <h3 className="text-lg font-semibold mb-2 text-strong" data-testid={`widget-name-${widget.id}`}>
                  {widget.name}
                </h3>
                <p className="text-sm text-muted mb-4 leading-relaxed" data-testid={`widget-description-${widget.id}`}>
                  {widget.description}
                </p>
                <ul className="space-y-2 mb-6">
                  {widget.features.map((feature) => (
                    <li key={`${widget.id}-${feature}`} className="flex items-center space-x-2 text-sm text-default" data-testid={`widget-feature-${widget.id}-${feature}`}>
                      <div className="w-1 h-1 brand-green rounded-full" style={{ backgroundColor: '#39FF14' }}></div>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                <div className="flex justify-between items-center pt-4 border-t border-subtle">
                  <div className="flex flex-col">
                    {isRandomizer ? (
                      <>
                        <span className="text-[10px] text-faded tracking-widest uppercase font-semibold">From</span>
                        <div className="flex items-baseline space-x-1">
                          <span className="text-2xl font-bold brand-green" style={{ color: '#39FF14' }} data-testid={`widget-price-${widget.id}`}>
                            {widget.id === 'widget-alert-banner' ? '$49' : '$79'}
                          </span>
                          <span className="text-xs text-faded">/ one-time</span>
                        </div>
                      </>
                    ) : (
                      <>
                        {(widget.id === 'widget-scoreboard' || widget.id === 'widget-alert-banner') && (
                          <span className="text-[10px] text-faded tracking-widest uppercase font-semibold" data-testid={`widget-starting-at-${widget.id}`}>
                            Starting at
                          </span>
                        )}
                        <div className="flex items-baseline space-x-2">
                          <span className="text-base text-faded line-through" data-testid={`widget-original-price-${widget.id}`}>
                            ${widget.price}
                          </span>
                          <span className="text-2xl font-bold brand-green" style={{ color: '#39FF14' }} data-testid={`widget-price-${widget.id}`}>
                            $69
                          </span>
                        </div>
                      </>
                    )}
                  </div>
                  <span
                    className="btn-primary px-4 py-2 text-sm inline-flex items-center space-x-1.5"
                    data-testid={`widget-buy-button-${widget.id}`}
                  >
                    {isRandomizer ? (
                      <>
                        <span>Learn more</span>
                        <ArrowRight className="w-4 h-4" />
                      </>
                    ) : (
                      <>
                        <ShoppingCart className="w-4 h-4" />
                        <span>Request</span>
                      </>
                    )}
                  </span>
                </div>
              </div>
            </Wrapper>
            );
          })}
        </div>
      </div>
    </section>
  );
};
